import React, { useEffect, useState } from "react";
import { outreachApi } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Mail, Plus, Trash2, ShieldAlert, Send, Target, Printer } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { CountyWinMap } from "./CountyWinMap";
import { ReplyDraftButton } from "./ReplyTemplates";

const stageColor = {
  NOT_CONTACTED: "bg-slate-100 text-slate-600 border-slate-200",
  EMAILED_1: "bg-blue-100 text-blue-700 border-blue-200",
  FOLLOWUP_1: "bg-violet-100 text-violet-700 border-violet-200",
  FOLLOWUP_2: "bg-indigo-100 text-indigo-700 border-indigo-200",
  REPLIED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  OPTED_OUT: "bg-red-100 text-red-700 border-red-200",
  BOUNCED: "bg-red-100 text-red-700 border-red-200",
  DONE: "bg-slate-200 text-slate-500 border-slate-300",
};

const CALL_STATUSES = [
  ["", "Not called"],
  ["LEFT_MESSAGE", "Left message"],
  ["SPOKE_INTERESTED", "Spoke — interested"],
  ["SEND_INFO", "Asked for info"],
  ["NOT_INTERESTED", "Not interested"],
];

const Stat = ({ label, value, accent = "#0E5A72" }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-3 py-2.5 text-center">
    <div className="font-display font-extrabold text-xl" style={{ color: accent }}>{value}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
  </div>
);

const OutreachPanel = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [dash, setDash] = useState(null);
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [view, setView] = useState("contacts");
  const [draft, setDraft] = useState({ org: "", name: "", role: "", email: "", county: "", category: "Hospital", payer: "", sourceUrl: "" });
  const [busy, setBusy] = useState("");

  const load = () => {
    outreachApi.settings().then((r) => setSettings(r.data)).catch(() => {});
    outreachApi.dashboard().then((r) => setDash(r.data)).catch(() => {});
    outreachApi.contacts().then((r) => setData(r.data)).catch(() => {});
    outreachApi.activity().then((r) => setActivity(r.data)).catch(() => {});
    outreachApi.keywords().then((r) => setKeywords(r.data.keywords)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setBusy("test");
    try {
      await outreachApi.testEmail("info@myohcare.com");
      toast({ title: "Test email sent to info@myohcare.com", description: "Once you confirm receipt, you can activate the campaign." });
      load();
    } catch { toast({ title: "Test email failed", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const togglePause = async (active) => {
    try {
      const r = await outreachApi.updateSettings({ paused: !active });
      setSettings(r.data);
      toast({ title: r.data.paused ? "Campaign paused" : "Campaign ACTIVE — daily batches will send on business days" });
    } catch (e) {
      toast({ title: "Cannot activate", description: e?.response?.data?.detail || "Send a test email first.", variant: "destructive" });
    }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const r = await outreachApi.runDaily();
      toast({ title: `Batch complete — ${r.data.sent} email(s) sent`, description: r.data.skipped || "Follow-ups scheduled automatically." });
      load();
    } catch { toast({ title: "Batch failed", variant: "destructive" }); }
    finally { setBusy(""); }
  };

  const printCallSheet = () => {
    const rows = (data.contacts || []).filter((c) => c.phone && !c.email && (!c.callStatus || c.callStatus === "LEFT_MESSAGE"));
    const trs = rows.map((c) => `<tr><td>${c.org}${c.callStatus === "LEFT_MESSAGE" ? " <em>(left message)</em>" : ""}</td><td>${c.county || ""}</td><td class="ph">${c.phone}</td><td class="notes"></td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Phone Outreach Call Sheet — Caregivers Alliance Homecare</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#0E2A33}h1{font-size:20px}p{font-size:12px;color:#555}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-top:12px}th,td{border:1px solid #cbd5e1;padding:7px 8px;text-align:left}
      th{background:#0E5A72;color:#fff}.ph{white-space:nowrap;font-weight:bold}.notes{width:34%}</style></head><body>
      <h1>Phone Outreach Call Sheet (${rows.length} organizations)</h1>
      <p>These organizations publish no email address — introduce Caregivers Alliance Homecare by phone. Script: "Hi, this is [name] from Caregivers Alliance Homecare, a DODD-certified home care provider now accepting new clients statewide. Who handles provider information or referrals?" Log outcomes in the owner dashboard.</p>
      <table><tr><th>Organization</th><th>County</th><th>Phone</th><th>Call notes / outcome</th></tr>${trs}</table>
      <script>window.print()</script></body></html>`);
    w.document.close();
  };

  const addContact = async () => {
    if (!draft.org.trim()) return;
    try {
      const r = await outreachApi.createContact(draft);
      setData((d) => ({ ...d, contacts: [r.data, ...d.contacts] }));
      setDraft({ org: "", name: "", role: "", email: "", county: "", category: "Hospital", payer: "", sourceUrl: "" });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not add contact", variant: "destructive" }); }
  };

  const patchContact = async (id, changes) => {
    try {
      const r = await outreachApi.updateContact(id, changes);
      setData((d) => ({ ...d, contacts: d.contacts.map((c) => (c.id === id ? r.data : c)) }));
      if (changes.referralReceived === true && r.data.milestone) {
        toast({ title: "🎉 Milestone!", description: `Your ${r.data.milestone} — congratulations! Alert email sent.` });
      } else if (changes.clientStart === true && r.data.milestone) {
        toast({ title: "🎉 Milestone!", description: `Your ${r.data.milestone} — congratulations! Alert email sent.` });
      }
      if (changes.callStatus === "SPOKE_INTERESTED") {
        if (r.data.onePagerSent) {
          toast({ title: "One-pager sent!", description: `Provider info emailed to ${r.data.email} (you're BCC'd).` });
        } else if (!r.data.email) {
          toast({ title: "Great call! Add their email to auto-send the one-pager", description: "Edit the contact and enter the email address they gave you." });
        }
      }
    } catch { toast({ title: "Update failed", variant: "destructive" }); }
  };

  const removeContact = async (id) => {
    try {
      await outreachApi.removeContact(id);
      setData((d) => ({ ...d, contacts: d.contacts.filter((c) => c.id !== id) }));
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  const patchKeyword = async (id, changes) => {
    setKeywords((ks) => ks.map((k) => (k.id === id ? { ...k, ...changes } : k)));
    try { await outreachApi.updateKeyword(id, changes); } catch {}
  };

  if (!settings || !dash || !data) return <div className="text-center text-slate-400 py-10">Loading outreach engine…</div>;

  const pct = Math.min(100, (dash.clientStarts / dash.clientTarget) * 100);

  return (
    <div data-testid="outreach-panel" className="space-y-5">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3 text-sm text-amber-800">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div><b>Compliance:</b> verified professional contacts only (official websites) — never scrape families, buy lists, guess emails or collect patient info. Campaign stays paused until a test email is confirmed. Sending stops automatically on reply, bounce or unsubscribe. Max 15/business day.</div>
      </div>

      {/* Campaign controls */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className={`border text-xs px-3 py-1.5 ${settings.paused ? "bg-red-100 text-red-700 border-red-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>
            {settings.paused ? "CAMPAIGN PAUSED" : "CAMPAIGN ACTIVE"}
          </Badge>
          <Badge className={`border text-xs px-3 py-1.5 ${settings.testPassed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
            Test email: {settings.testPassed ? "confirmed" : "not sent"}
          </Badge>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Switch data-testid="campaign-active-switch" checked={!settings.paused} onCheckedChange={togglePause} /> Active
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            Daily limit
            <Input type="number" value={settings.dailyLimit} className="h-8 w-16"
              onChange={(e) => outreachApi.updateSettings({ dailyLimit: parseInt(e.target.value || "12", 10) }).then((r) => setSettings(r.data)).catch(() => {})} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="print-callsheet-btn" size="sm" variant="outline" onClick={printCallSheet} className="rounded-full border-slate-300 text-slate-700">
            <Printer className="mr-2 h-4 w-4" /> Print call sheet
          </Button>
          <Button data-testid="outreach-test-btn" size="sm" variant="outline" disabled={busy === "test"} onClick={sendTest} className="rounded-full border-[#0E5A72] text-[#0E5A72]">
            <Mail className="mr-2 h-4 w-4" /> {busy === "test" ? "Sending…" : "Send test email"}
          </Button>
          <Button data-testid="outreach-run-btn" size="sm" disabled={busy === "run" || !settings.testPassed} onClick={runNow} className="rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white">
            <Send className="mr-2 h-4 w-4" /> {busy === "run" ? "Sending…" : "Run today's batch now"}
          </Button>
        </div>
      </div>

      {/* 30-client target */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2"><Target className="h-5 w-5 text-[#2E9E6A]" /> Progress toward 30 qualified client starts / commitments by December 31, 2026</h3>
          <span className="font-display font-extrabold text-xl text-[#0E5A72]">{dash.clientStarts} / {dash.clientTarget}</span>
        </div>
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden mt-2">
          <div className="h-full rounded-full bg-[#2E9E6A]" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Target, not a guaranteed result. Counts leads at Ready to Start/Won plus contacts marked Client Start.</p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-2">
        <Stat label="Contacts" value={dash.contactsTotal} />
        <Stat label="Sent today" value={dash.emailsSentToday} />
        <Stat label="Sent total" value={dash.emailsSentTotal} />
        <Stat label="Opened" value={dash.opens || 0} accent="#7C3AED" />
        <Stat label="Replies" value={dash.replies} accent="#2E9E6A" />
        <Stat label="Bounces" value={dash.bounces} accent="#DC2626" />
        <Stat label="Opt-outs" value={dash.optOuts} accent="#DC2626" />
        <Stat label="Referrals" value={dash.referralsReceived} accent="#2E9E6A" />
        <Stat label="Consults" value={dash.consultations} accent="#7C3AED" />
        <Stat label="Client starts" value={dash.clientStarts} accent="#2E9E6A" />
      </div>

      <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 shadow-sm">
        {["contacts", "results", "keywords"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`outreach-view-${v}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${view === v ? "bg-[#0E5A72] text-white" : "text-slate-600"}`}>
            {v === "contacts" ? "Professional Contacts" : v === "results" ? "Campaign Results" : "Keyword Dashboard"}
          </button>
        ))}
      </div>

      {view === "contacts" && (
        <>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 grid sm:grid-cols-2 lg:grid-cols-8 gap-2 items-end">
            <Input data-testid="contact-org-input" value={draft.org} onChange={(e) => setDraft({ ...draft, org: e.target.value })} placeholder="Organization *" className="h-9 lg:col-span-2" />
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Contact name" className="h-9" />
            <Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Verified email" className="h-9" />
            <Input value={draft.county} onChange={(e) => setDraft({ ...draft, county: e.target.value })} placeholder="County" className="h-9" />
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={draft.payer || "none"} onValueChange={(v) => setDraft({ ...draft, payer: v === "none" ? "" : v })}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Payer" /></SelectTrigger>
              <SelectContent>{data.payers.map((p) => <SelectItem key={p || "none"} value={p || "none"}>{p || "No payer"}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="contact-add-btn" onClick={addContact} className="bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-9"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {data.contacts.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <span className="font-medium text-[#0E2A33] text-sm">{c.org}</span>
                  <span className="text-xs text-slate-400 ml-2">{c.name} {c.role && `· ${c.role}`} {c.email && `· ${c.email}`} {c.county && `· ${c.county}`} {c.payer && `· ${c.payer}`}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {c.phone && !c.email ? (
                    <>
                      <span className="text-xs font-semibold text-[#0E5A72]">{c.phone}</span>
                      <Input placeholder="add verified email" className="h-7 w-40 text-xs"
                        onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.includes("@")) patchContact(c.id, { email: e.target.value.trim() }); }}
                        onBlur={(e) => { if (e.target.value.includes("@")) patchContact(c.id, { email: e.target.value.trim() }); }} />
                      <Select value={c.callStatus || ""} onValueChange={(v) => patchContact(c.id, { callStatus: v === "__none" ? "" : v })}>
                        <SelectTrigger data-testid="call-status-select" className="h-7 w-40 text-xs"><SelectValue placeholder="Call outcome" /></SelectTrigger>
                        <SelectContent>
                          {CALL_STATUSES.map(([v, label]) => <SelectItem key={v || "__none"} value={v || "__none"}>{label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  ) : (
                    <Badge className={`border text-[10px] ${stageColor[c.stage] || ""}`}>{c.stage.replace(/_/g, " ")}</Badge>
                  )}
                  <label className="flex items-center gap-1 text-xs text-slate-600"><Switch checked={c.replied} onCheckedChange={(v) => patchContact(c.id, { replied: !!v })} /> Replied</label>
                  <label className="flex items-center gap-1 text-xs text-slate-600"><Switch checked={c.referralReceived} onCheckedChange={(v) => patchContact(c.id, { referralReceived: !!v })} /> Referral</label>
                  <label className="flex items-center gap-1 text-xs text-slate-600"><Switch checked={c.clientStart} onCheckedChange={(v) => patchContact(c.id, { clientStart: !!v })} /> Client start</label>
                  <label className="flex items-center gap-1 text-xs text-red-600"><Switch checked={c.optOut} onCheckedChange={(v) => patchContact(c.id, { optOut: !!v })} /> Opt-out</label>
                  <Button size="icon" variant="outline" className="h-7 w-7 text-red-600 border-red-200" onClick={() => removeContact(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
            {data.contacts.length === 0 && <div className="text-center text-slate-400 py-8 bg-white rounded-2xl border border-slate-100">Add verified professional contacts (official sources only) to start the campaign.</div>}
          </div>
        </>
      )}

      {view === "results" && activity && (
        <>
        <CountyWinMap wins={dash.countyWins || {}} contacts={data.contacts || []} />
        <div data-testid="outreach-results-view" className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h4 className="font-display font-bold text-[#0E2A33] text-sm mb-3">📅 Upcoming sends ({activity.upcoming.length})</h4>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {activity.upcoming.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-[#F7FBFC] rounded-lg px-3 py-2">
                  <span className="font-medium text-[#0E2A33] truncate mr-2">{c.org}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    {c.opens > 0 && <Badge className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]">Opened ×{c.opens}</Badge>}
                    <Badge className={`border text-[10px] ${stageColor[c.stage] || ""}`}>{c.stage.replace(/_/g, " ")}</Badge>
                    <span className="text-slate-500 font-medium">{c.nextFollowUpAt ? new Date(c.nextFollowUpAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</span>
                  </span>
                </div>
              ))}
              {activity.upcoming.length === 0 && <p className="text-xs text-slate-400">No follow-ups scheduled yet.</p>}
            </div>
            <h4 className="font-display font-bold text-[#0E2A33] text-sm mb-2 mt-5">⏳ Queued — never contacted ({activity.queued.length})</h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {activity.queued.map((c, i) => (
                <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5">{c.org} <span className="text-slate-400">· {c.county}</span></div>
              ))}
              {activity.queued.length === 0 && <p className="text-xs text-slate-400">Everyone with an email has been contacted.</p>}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <h4 className="font-display font-bold text-[#0E2A33] text-sm mb-3">✅ Replied / interested — one-click reply drafts</h4>
            <div className="space-y-1.5 max-h-56 overflow-y-auto mb-5">
              {data.contacts.filter((c) => c.replied || c.callStatus === "SPOKE_INTERESTED").map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-xs text-emerald-800 bg-emerald-50 rounded-lg px-3 py-1.5">
                  <span className="font-medium truncate">{c.org}</span>
                  <ReplyDraftButton contact={c} />
                </div>
              ))}
              {data.contacts.filter((c) => c.replied || c.callStatus === "SPOKE_INTERESTED").length === 0 &&
                <p className="text-xs text-slate-400">No replies yet — reply sync checks the inbox every 30 minutes.</p>}
            </div>
            <h4 className="font-display font-bold text-[#0E2A33] text-sm mb-3">📨 Recent activity</h4>
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {activity.recent.map((l, i) => (
                <div key={i} className="text-xs bg-[#F7FBFC] rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#0E2A33] truncate mr-2">{l.org}</span>
                    <span className="text-slate-400 shrink-0">{l.sentAt ? new Date(l.sentAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
                  </div>
                  <div className="text-slate-500 truncate">{l.subject}</div>
                </div>
              ))}
              {activity.recent.length === 0 && <p className="text-xs text-slate-400">No emails sent yet.</p>}
            </div>
          </div>
        </div>
        </>
      )}

      {view === "keywords" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 overflow-x-auto">
          <p className="text-xs text-slate-400 mb-3">Enter real ranking data only (from Google Search Console once connected). Never invent volumes or rankings.</p>
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="py-2">Keyword</th><th>Rank</th><th>Prev</th><th>Page</th><th>Impr.</th><th>Clicks</th><th>Leads</th><th>Checked</th>
            </tr></thead>
            <tbody>
              {keywords.map((k) => (
                <tr key={k.id} className="border-b border-slate-50">
                  <td className="py-1.5 font-medium text-[#0E2A33]">{k.keyword}</td>
                  <td><Input value={k.currentRank || ""} onChange={(e) => patchKeyword(k.id, { currentRank: e.target.value })} className="h-7 w-14 text-xs" /></td>
                  <td className="text-xs text-slate-400">{k.previousRank || "—"}</td>
                  <td><Input value={k.page || ""} onChange={(e) => patchKeyword(k.id, { page: e.target.value })} className="h-7 w-32 text-xs" placeholder="/dodd" /></td>
                  <td><Input type="number" value={k.impressions || 0} onChange={(e) => patchKeyword(k.id, { impressions: parseInt(e.target.value || "0", 10) })} className="h-7 w-16 text-xs" /></td>
                  <td><Input type="number" value={k.clicks || 0} onChange={(e) => patchKeyword(k.id, { clicks: parseInt(e.target.value || "0", 10) })} className="h-7 w-14 text-xs" /></td>
                  <td><Input type="number" value={k.leads || 0} onChange={(e) => patchKeyword(k.id, { leads: parseInt(e.target.value || "0", 10) })} className="h-7 w-14 text-xs" /></td>
                  <td><Input type="date" value={k.lastChecked || ""} onChange={(e) => patchKeyword(k.id, { lastChecked: e.target.value })} className="h-7 w-32 text-xs" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OutreachPanel;
